/**
 * Supabase 客戶端配置
 */

import { createClient } from '@supabase/supabase-js'
import { env } from './env'
import { logger } from '../utils/logger'

// Supabase 配置
const supabaseUrl = env.supabaseUrl
const supabaseAnonKey = env.supabaseAnonKey

// 環境信息（用於調試）
const environment = env.environment || 'development'

if (!supabaseUrl || !supabaseAnonKey) {
  logger.warn('⚠️ Supabase 配置未設置，請在環境變數中設置 VITE_SUPABASE_URL 和 VITE_SUPABASE_ANON_KEY')
  logger.warn(`   當前環境: ${environment}`)
} else if (env.enableDebug || env.isDev) {
  logger.log(`✅ Supabase 已配置 (環境: ${environment})`)
}

// 創建 Supabase 客戶端（前端使用）
export const supabase = createClient(
  supabaseUrl || 'https://placeholder.supabase.co',
  supabaseAnonKey || 'placeholder-key',
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true
    },
    db: {
      schema: 'public'
    },
    global: {
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'apikey': supabaseAnonKey || 'placeholder-key',
        'Prefer': 'return=representation'
      }
    }
  }
)

// 數據庫類型定義（後續會根據實際 Schema 更新）
export type Database = {
  public: {
    Tables: {
      users: {
        Row: {
          wallet_address: string
          created_at: string
          total_investments: number
          total_amount: string
          investment_count: number
          referral_code: string | null
        }
        Insert: {
          wallet_address: string
          created_at?: string
          total_investments?: number
          total_amount?: string
          investment_count?: number
          referral_code?: string | null
        }
        Update: {
          wallet_address?: string
          created_at?: string
          total_investments?: number
          total_amount?: string
          investment_count?: number
          referral_code?: string | null
        }
      }
      investments: {
        Row: {
          id: string
          investor_address: string
          amount: string
          token_amount: string
          tx_hash: string
          stage: string
          referrer_address: string | null
          referral_reward: string | null
          root_referrer_address: string | null
          root_referral_reward: string | null
          referral_code: string | null
          token_reward: string | null
          status: 'pending' | 'confirmed' | 'failed'
          created_at: string
        }
        Insert: {
          id?: string
          investor_address: string
          amount: string
          token_amount: string
          tx_hash: string
          stage: string
          referrer_address?: string | null
          referral_reward?: string | null
          root_referrer_address?: string | null
          root_referral_reward?: string | null
          referral_code?: string | null
          token_reward?: string | null
          status?: 'pending' | 'confirmed' | 'failed'
          created_at?: string
        }
        Update: {
          id?: string
          investor_address?: string
          amount?: string
          token_amount?: string
          tx_hash?: string
          stage?: string
          referrer_address?: string | null
          referral_reward?: string | null
          root_referrer_address?: string | null
          root_referral_reward?: string | null
          referral_code?: string | null
          token_reward?: string | null
          status?: 'pending' | 'confirmed' | 'failed'
          created_at?: string
        }
      }
      referrals: {
        Row: {
          id: string
          referrer_address: string
          referee_address: string
          referral_code: string | null
          created_at: string
          total_reward: string
          reward_status: 'pending' | 'paid'
        }
        Insert: {
          id?: string
          referrer_address: string
          referee_address: string
          referral_code?: string | null
          created_at?: string
          total_reward?: string
          reward_status?: 'pending' | 'paid'
        }
        Update: {
          id?: string
          referrer_address?: string
          referee_address?: string
          referral_code?: string | null
          created_at?: string
          total_reward?: string
          reward_status?: 'pending' | 'paid'
        }
      }
      referrers: {
        Row: {
          id: string
          referrer_address: string
          referral_code: string
          name: string | null
          commission_rate: string
          is_active: boolean
          total_referrals: number
          total_commission: string
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          referrer_address: string
          referral_code: string
          name?: string | null
          commission_rate?: string
          is_active?: boolean
          total_referrals?: number
          total_commission?: string
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          referrer_address?: string
          referral_code?: string
          name?: string | null
          commission_rate?: string
          is_active?: boolean
          total_referrals?: number
          total_commission?: string
          created_at?: string
          updated_at?: string
        }
      }
      stages: {
        Row: {
          id: string
          name: string
          price: string
          total_tokens: string
          sold_tokens: string
          start_time: string
          end_time: string | null
          is_active: boolean
        }
        Insert: {
          id?: string
          name: string
          price: string
          total_tokens: string
          sold_tokens?: string
          start_time: string
          end_time?: string | null
          is_active?: boolean
        }
        Update: {
          id?: string
          name?: string
          price?: string
          total_tokens?: string
          sold_tokens?: string
          start_time?: string
          end_time?: string | null
          is_active?: boolean
        }
      }
      fundraising: {
        Row: {
          id: string
          total_raised: string
          total_tokens_sold: string
          total_investors: number
          current_stage: string
          updated_at: string
        }
        Insert: {
          id?: string
          total_raised?: string
          total_tokens_sold?: string
          total_investors?: number
          current_stage?: string
          updated_at?: string
        }
        Update: {
          id?: string
          total_raised?: string
          total_tokens_sold?: string
          total_investors?: number
          current_stage?: string
          updated_at?: string
        }
      }
    }
  }
}

